import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useRecoilValue } from 'recoil';
import {
  CmdAlert,
  CmdCalendar,
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdTextarea,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import FormikValidatorService from '../../Services/FormikValidatorService';
import PermissionsService, { PERMISSION_REFUELING_CARDS } from '../../Services/PermissionsService';
import TableService, { TABLE_FUEL_CARD } from '../../Services/TableService';
import { IFuelCardsInput } from '.';
import usePageWithForm from '../../hooks/usePageWithForm';
import SimpleSelectWithSearchHookForm from '../Forms/Fields/SimpleSelectWithSearchHookForm';
import FuelCardsService from './FuelCardsService';
import {
  getFuelCardsOptions,
  getFuelCardsValues,
} from '../VehiclesSettings/VehicleSettingsService';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import useAnalytics from '../../hooks/useAnalytics';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ModalFormProps {
  handleClose: any;
  // data: any;
  id: number | null;
  customerId: number | null;
  updateTable: any;
}

type TFuelCardsFormKeys =
  | 'customerId'
  | 'fuelCardProviderId'
  | 'cardNumber'
  | 'identifier'
  | 'validUntil'
  | 'vehicleId'
  | 'note';

interface IFuelCardsFormValues {
  customerId: string | number;
  fuelCardProviderId: '';
  cardNumber: string;
  identifier: string;
  validUntil: Date;
  vehicleId: string;
  note: string;
}

function Form(props: ModalFormProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  usePageWithForm();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const { formatMessage: f } = useIntl();
  const { id } = props;
  const [validation, setValidation] = React.useState<any>(null);
  const [selectOptions, setSelectOptions] = React.useState<any>({
    vehicleId: [],
    fuelCardProviderId: [],
    customerId: [],
  });
  const [isAlertShow, setIsAlertShow] = React.useState(false);
  const [dataAlert, setDataAlert] = React.useState<{
    title: string;
    message: string;
    type: string;
  } | null>(null);

  const handleInitDatepickerValue = () => {
    const date = new Date();
    const month = date.getMonth();
    const year = date.getFullYear();
    date.setMonth(month + 1);
    date.setFullYear(year + 2);
    return date;
  };

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    trigger,
    watch,
    setFocus,
    clearErrors,
    formState: { errors },
  } = useForm<IFuelCardsFormValues>({
    mode: 'onChange',
    defaultValues: {
      customerId: selectedCustomers.length === 1 ? selectedCustomers[0] : '',
      fuelCardProviderId: '',
      cardNumber: '',
      identifier: '',
      validUntil: handleInitDatepickerValue(),
      vehicleId: '',
      note: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleFormData = (data: IFuelCardsInput[], type: string) => {
    const validUntil = data.find((i) => i.indexId === 'validUntil')?.[type];
    if (typeof validUntil === 'string') {
      setValue('validUntil', new Date(validUntil));
    }
    return {
      fuelCardProviderId: data.find((i) => i.indexId === 'fuelCardProviderId')?.[type],
      cardNumber: data.find((i) => i.indexId === 'cardNumber')?.[type],
      identifier: data.find((i) => i.indexId === 'identifier')?.[type],
      validUntil,
      customerId: data.find((i) => i.indexId === 'customerId')?.[type],
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.[type],
      note: data.find((i) => i.indexId === 'note')?.[type],
    };
  };

  // create
  const handleErrorResponse = (response: AxiosResponse) => {
    const responseValidation = handleFormData(response.data.form.inputs, 'rules');

    setValidation(responseValidation);

    const fields = Object.keys(getValues());
    fields.forEach((field) => {
      const filteredValidation = responseValidation[field].find(
        (item) => item.param === 'assertTrue' && !item.value
      );
      if (filteredValidation) {
        setError(field as TFuelCardsFormKeys, { message: filteredValidation.message });
        setFocus(field as TFuelCardsFormKeys, { shouldSelect: true });
      }
    });
  };

  const handleCloseForm = () => {
    trackEvent(['Fuel cards form', 'click', 'Close form']);
    props.handleClose();
    setIsAlertShow(false);
    setDataAlert(null);
  };

  const create = async (createValues: any) => {
    trackEvent(['Fuel cards form', 'click', 'Create fuel card']);

    setIsLoaderActive(true);

    try {
      const response = await FuelCardsService.createFuelCard({ ...createValues });

      if ((response as AxiosResponse).data && (response as AxiosResponse).status === 400) {
        handleErrorResponse(response as AxiosResponse);
      } else if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
        handleCloseForm();
        props.updateTable();
      }
    } finally {
      setIsLoaderActive(false);
    }
  };

  // update
  const update = async (updateValues: any) => {
     trackEvent(['Fuel cards form', 'click', 'Update fuel card']);

    const newValues = {
      ...updateValues,
      customerId: props.customerId,
      // validUntil: datepickerValue,
    };
    setIsLoaderActive(true);

    try {
      const response = await FuelCardsService.updateFuelCard(id, newValues);

      if ((response as AxiosResponse).data && (response as AxiosResponse).status === 400) {
        handleErrorResponse(response as AxiosResponse);
      } else if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
        handleCloseForm();
        props.updateTable();
      }
    } finally {
      setIsLoaderActive(false);
    }
  };

  const handleOptionsData = (data: IFuelCardsInput[]) => {
    const tempCustomerId: { value: number; label: string; item: string }[] =
      data.find((i) => i.indexId === 'customerId')?.values || [];

    const filteredOptionByPermissions = tempCustomerId.filter((item) =>
      PermissionsService.hasAccess(
        PERMISSION_REFUELING_CARDS,
        props.id ? ['read', 'update'] : ['create'],
        item.value
      )
    );

    return {
      vehicleId: data.find((i) => i.indexId === 'vehicleId')?.values || [],
      customerId: filteredOptionByPermissions,
      fuelCardProviderId: data.find((i) => i.indexId === 'fuelCardProviderId')?.values || [],
    };
  };

  const resendDynamicForm = React.useCallback(async () => {
    setIsLoaderActive(true);
    const response = !props.id
      ? await TableService.resendDynamicForm(TABLE_FUEL_CARD, getValues())
      : await TableService.resendDynamicFormById(
          TABLE_FUEL_CARD,
          props.id,
          watch('cardNumber') && watch('identifier') && watch('customerId')
            ? getValues()
            : { customerId: props.customerId },
          props.customerId
        );
    if (response && response.form && response.form.inputs) {
      const responseData = handleFormData(response.form.inputs, 'value');

      Object.keys(responseData).forEach((fieldName) => {
        setValue(fieldName as TFuelCardsFormKeys, responseData[fieldName]);
      });

      setValidation(handleFormData(response.form.inputs, 'rules'));
      setSelectOptions(handleOptionsData(response.form.inputs));
      setIsLoaderActive(false);

      return response.form.inputs;
    }
    setIsLoaderActive(false);
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseAlert = React.useCallback(() => {
    trackEvent(['Fuel cards form', 'click', 'Close alert']);
    setIsAlertShow(false);
    setDataAlert(null);
  }, []);

  const results = useQueries({
    queries: [
      {
        queryKey: ['FuelCardsFormData', props.id, props.customerId],
        queryFn: () => resendDynamicForm(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });
  const { data: fuelCardsData, isLoading: isLoadingSupplierData } = results[0];

  const getVehicleValue = (): number | string => {
    const vehicle = fuelCardsData?.find((i) => i.indexId === 'vehicleId');

    if (props.id) {
      const v = vehicle?.values?.find(
        (item: any) => String(item.value) === String(watch('vehicleId'))
      );

      if (v) {
        return v.item;
      }
      return '';
    }
    if (vehicle && vehicle.value) {
      return vehicle.value;
    }
    if (selectedVehicles.length === 1) {
      return String(selectedVehicles[0]);
    }
    return '';
  };

  const getLabelField = (type: string) => {
    let value = getValues()[type];

    if (selectOptions[type]) {
      if (value && Object.keys(value).length > 0) {
        const filteredItems = selectOptions[type].filter((i: any) => value.includes(i.value));
        if (filteredItems && filteredItems[0] && filteredItems[0].item) {
          value = filteredItems.map((i: any) => {
            return <div key={i.id}>{i.item}</div>;
          });
        }
      } else if (!value) {
        if (selectOptions[type] && selectOptions[type][0] && selectOptions[type][0].item) {
          value = selectOptions[type][0].item;
        }
      } else {
        const filteredItem = selectOptions[type].filter((i: any) => i.value === value);
        if (filteredItem && filteredItem[0] && filteredItem[0].item) {
          value = filteredItem[0].item;
        }
      }
    }

    return value;
  };

  let isAllowed = true;
  // modal adding title and buttons
  let buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: isAllowed ? f({ id: 'form.cancel' }) : f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: handleCloseForm,
      tooltip: isAllowed
        ? f({ id: 'form.cancel.tooltip' })
        : f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(create),
      tooltip: f({ id: 'alarms.modal.recipient.save' }),
    },
  ];

  // modal editing title and buttons
  if (id) {
    isAllowed = PermissionsService.hasAccess(
      PERMISSION_REFUELING_CARDS,
      ['update'],
      props.customerId
    );

    buttons = [
      {
        id: 'modalClose',
        type: 'button',
        title: isAllowed ? f({ id: 'form.cancel' }) : f({ id: 'form.close' }),
        className: 'e-button',
        closeCallback: handleCloseForm,
        tooltip: isAllowed
          ? f({ id: 'form.cancel.tooltip' })
          : f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
      },
    ];
    if (isAllowed) {
      buttons.push({
        id: 'submitEditForm',
        type: 'submit',
        title: f({ id: 'form.save' }),
        className: 'e-button e-button--gray',
        submitCallback: handleSubmit(update),
      });
    }
  }
  // isAllowed = false;

  const handleCustomer = (customerId: string | number) => {
    trackEvent(['Fuel cards form', 'change', 'Customer', Number(customerId)]);
    setValue('customerId', customerId);
    setValue('vehicleId', '');
    trigger('customerId');
    resendDynamicForm();
  };

  const getFuelCardOptions = async (assignedValues: string[], customerId: string | number) => {
    const response = await getFuelCardsOptions(String(customerId), Number(watch('vehicleId')));
    if (response) {
      const messages: string[] = [];
      const filtedCards = response.filter((item) => assignedValues.includes(String(item.value)));

      filtedCards.map((item, index) => {
        if (index === 0) {
          messages.push(
            `<br />${
              item.providerName
                ? `- ${item.providerName}-${item.item}-${item.cardNumber}`
                : `- ${item.item}-${item.cardNumber}`
            }`
          );
        } else {
          messages.push(
            `${
              item.providerName
                ? `- ${item.providerName}-${item.item}-${item.cardNumber}`
                : `- ${item.item}-${item.cardNumber}`
            }`
          );
        }
      });

      const fuelCardsMessage = messages.join(', <br />');

      setDataAlert({
        title: f({ id: 'toastr.warning' }),
        message: `${f({
          id: 'form.inputs.fuelCards.provider.vehicle.warning',
        })} ${fuelCardsMessage}.`,
        type: 'warning',
      });

      setIsAlertShow(true);
      return response;
    }
    setIsAlertShow(false);
    return false;
  };

  const getSelectedCards = async () => {
    const response = await getFuelCardsValues(Number(watch('vehicleId')));
    if (response) {
      const selectedFuelCard = response;
      if (selectedFuelCard.length > 0 && watch('customerId')) {
        const assignedCards = response;
        getFuelCardOptions(
          assignedCards,
          props.id ? String(props.customerId) : watch('customerId')
        );
      }

      return response;
    }

    return false;
  };

  const isLoading = isLoaderActive || isLoadingSupplierData;

  return (
    <CmdModal
      id="fuel-cards-form"
      title={props.id ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      footerButtons={buttons}
      loader={isLoading}
    >
      {isAlertShow && dataAlert && (
        <CmdAlert
          id="fuel-card-alert"
          data={dataAlert}
          onCloseCallback={handleCloseAlert}
          isAbsolute
        />
      )}
      {isAllowed ? (
        <div>
          {props.id || selectedCustomers.length === 1 ? (
            <CmdLabel
              title={f({ id: 'form.inputs.customer' })}
              value={getLabelField('customerId')}
            />
          ) : (
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithSearchHookForm
                  {...field}
                  options={selectOptions.customerId || []}
                  id="customerId"
                  label={f({ id: 'form.inputs.customer' })}
                  introOption={f({ id: 'form.choose' })}
                  // required={true}
                  onChangeCallback={handleCustomer}
                  control={control}
                  required={FormikValidatorService.isFieldRequired(
                    validation ? validation.customerId : []
                  )}
                  error={errors.customerId?.message}
                  touched={true}
                  // canRemoveValue={true}
                />
              )}
            />
          )}{' '}
          {props.id ? (
            <CmdLabel
              title={f({ id: 'form.inputs.fuelCards.provider' })}
              value={watch('fuelCardProviderId')}
            />
          ) : (
            <Controller
              name="fuelCardProviderId"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithSearchHookForm
                  {...field}
                  options={selectOptions.fuelCardProviderId || []}
                  name="fuelCardProviderId"
                  id="fuelCardProviderId"
                  label={f({ id: 'form.inputs.fuelCards.provider' })}
                  introOption={f({ id: 'form.choose' })}
                  canRemoveValue={true}
                  control={control}
                  onChangeCallback={(value) => {
                    trackEvent(['Fuel cards form', 'change', 'Fuel card provider', Number(value)]);
                    setValue('fuelCardProviderId', value);
                    trigger('fuelCardProviderId');
                  }}
                />
              )}
            />
          )}
          <Controller
            name="cardNumber"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="cardNumber"
                label={f({ id: 'form.inputs.fuelCards.cardNumber' })}
                placeholder={f({ id: 'form.inputs.fuelCards.cardNumber.placeholder' })}
                type="text"
                required={FormikValidatorService.isFieldRequired(
                  validation ? validation.cardNumber : []
                )}
                onChangeCallback={(value) => {
                  setValue('cardNumber', value);
                  trigger('cardNumber');
                  const findAssertTrueValidation = validation.cardNumber?.find(
                    (item) => item.param === 'assertTrue' && !item.value
                  );

                  if (errors.cardNumber?.message && findAssertTrueValidation) {
                    setValidation((oldValidation) => {
                      const filteredValidation = oldValidation.cardNumber.filter(
                        (item) => item.param !== 'assertTrue'
                      );

                      return {
                        ...oldValidation,
                        cardNumber: filteredValidation,
                      };
                    });

                    clearErrors('cardNumber');
                    trigger('cardNumber');
                  }
                }}
                error={errors.cardNumber?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="identifier"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="identifier"
                label={f({ id: 'form.inputs.fuelCards.identifier' })}
                placeholder={f({ id: 'form.inputs.fuelCards.identifier.placeholder' })}
                type="text"
                onChangeCallback={(value) => {
                  setValue('identifier', value);
                  trigger('identifier');

                  const findAssertTrueValidation = validation.identifier?.find(
                    (item) => item.param === 'assertTrue' && !item.value
                  );

                  if (errors.identifier?.message && findAssertTrueValidation) {
                    setValidation((oldValidation) => {
                      const filteredValidation = oldValidation.identifier.filter(
                        (item) => item.param !== 'assertTrue'
                      );

                      return {
                        ...oldValidation,
                        identifier: filteredValidation,
                      };
                    });

                    clearErrors('identifier');
                    trigger('identifier');
                  }
                }}
                required={FormikValidatorService.isFieldRequired(
                  validation ? validation.identifier : []
                )}
                error={errors.identifier?.message}
                touched={true}
                tooltip={f({ id: 'form.inputs.fuelCards.identifier.tooltip' })}
              />
            )}
          />
          <Controller
            name="validUntil"
            control={control}
            render={({ field }) => (
              <CmdCalendar
                {...field}
                id="validUntil"
                calendarTranslations={{}}
                dateFormat="MM/yyyy"
                onChangeCallback={(value) => {
                  trackEvent(['Fuel cards form', 'change', 'Valid until']);
                  setValue('validUntil', value as Date);
                  trigger('validUntil');
                }}
                allowedRange={[
                  new Date(dayjs(new Date()).format('YYYY-MM-DD')),
                  new Date(dayjs(new Date()).add(14, 'year').format('YYYY-MM-DD')),
                ]}
                label={f({ id: 'table_list.columns.fuelCards.validUntil' })}
                tooltip={f({ id: 'form.inputs.fuelCards.validUntil.tooltip' })}
              />
            )}
          />
          <Controller
            name="vehicleId"
            control={control}
            render={({ field }) => (
              <SimpleSelectWithSearchHookForm
                {...field}
                options={selectOptions.vehicleId || []}
                id="vehicleId"
                label={f({ id: 'form.inputs.fuelCards.vehicle' })}
                introOption={
                  watch('customerId') || props.id
                    ? f({ id: 'form.placeholders.fuelCards.selectVehicle' })
                    : f({ id: 'form.placeholders.fuelCards.selectCustomerFirst' })
                }
                disabled={!watch('customerId') && selectedCustomers.length !== 1}
                tooltip={f({ id: 'form.inputs.fuelCards.vehicle.tooltip' })}
                canRemoveValue={true}
                control={control}
                onChangeCallback={(value) => {
                  setValue('vehicleId', value);
                  trigger('vehicleId');
                  trackEvent(['Fuel cards form', 'change', 'Vehicle', Number(value)]);
                  if (watch('vehicleId')) {
                    getSelectedCards();
                  }
                }}
              />
            )}
          />
          <Controller
            name="note"
            control={control}
            rules={{ required: errors.note ? errors.note.message : undefined }}
            render={({ field }) => (
              <CmdTextarea
                name={field.name}
                id="note"
                label={f({ id: 'form.inputs.note' })}
                rows={5}
                setValue={(value) => {
                  setValue('note', value);
                  trigger('note');
                }}
                value={field.value}
                error={errors.note?.message}
                touched={true}
              />
            )}
          />
        </div>
      ) : (
        <div className="form-horizontal">
          <CmdLabel
            title={f({ id: 'form.inputs.customer' })}
            value={getLabelField('customerId')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'form.inputs.fuelCards.provider' })}
            value={watch('fuelCardProviderId')}
            areLabels={true}
          />{' '}
          <CmdLabel
            title={f({ id: 'form.inputs.fuelCards.cardNumber' })}
            value={watch('cardNumber')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'form.inputs.fuelCards.identifier' })}
            value={watch('identifier')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'table_list.columns.fuelCards.validUntil' })}
            value={dayjs(watch('validUntil')).format('D.M.YYYY')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'form.inputs.fuelCards.vehicle' })}
            value={String(getVehicleValue())}
            areLabels={true}
          />
          <CmdLabel title={f({ id: 'form.inputs.note' })} value={watch('note')} areLabels={true} />
        </div>
      )}
    </CmdModal>
  );
}

export default React.memo(Form);
